/**
 * Turns the `asset-base-url` attribute into a `RenderOptions.resolveHref`.
 *
 * The renderer owns asset resolution — it understands that contract hrefs are
 * typically `s3://…` and renders a placeholder rather than a broken image when a
 * href cannot be mapped. This is the declarative shortcut for the common case:
 * a host that serves the manuscript's assets under one prefix.
 */

/** Schemes we must not rewrite: already loadable, or not ours to guess at. */
const ABSOLUTE = /^(?:https?:)?\/\//;
const DATA_URI = /^data:/;
const S3_URI = /^s3:\/\//;

export type HrefResolver = (href: string) => string | null;

/**
 * Joins a base URL and a relative href without doubling or dropping the slash.
 */
export function joinAssetUrl(baseUrl: string, href: string): string {
  return `${baseUrl.replace(/\/+$/, '')}/${href.replace(/^\/+/, '')}`;
}

/**
 * Resolver for a single base prefix.
 *
 * Returns `null` for `s3://` hrefs rather than guessing at bucket layout — the
 * renderer then shows its placeholder, which is a clearer failure than a broken
 * image. A host whose contracts carry `s3://` hrefs should set the `resolveHref`
 * property with real mapping logic instead of using this attribute.
 */
export function createBaseUrlResolver(baseUrl: string): HrefResolver {
  return (href: string): string | null => {
    if (!href) return null;
    if (DATA_URI.test(href) || ABSOLUTE.test(href)) return href;
    if (S3_URI.test(href)) return null;
    return joinAssetUrl(baseUrl, href);
  };
}
